/**
 * Web CV Health rings: the twin of `HealthRings.tsx`.
 *
 * Inline SVG with the geometry from `health-rings-geometry.ts`. Arcs start at
 * 12 o'clock and run clockwise; `[dir="rtl"]` mirrors the SVG so they grow
 * counter-clockwise in Arabic. A changed arc eases over 600 ms through a CSS
 * transition on `stroke-dashoffset` (`.tabbio-health-ring-arc` in
 * global.css), which `prefers-reduced-motion` turns off. The first mount while
 * checking starts from empty and fills.
 */
import { useEffect, useRef, useState } from 'react';
import {
  HEALTH_RING_REFERENCE_SIZE,
  clampFraction,
  describeRings,
  ringArcOpacity,
  ringDashOffset,
  ringStartTransform,
  type HealthRingGeometry,
} from './health-rings-geometry';
import type { HealthRingsProps } from './health-props';

export type { HealthRingsProps };

export function HealthRings({
  size = HEALTH_RING_REFERENCE_SIZE,
  mode,
  rings,
  center,
  accessibilityLabel,
  animate = true,
}: HealthRingsProps) {
  const layout = describeRings(size);
  const checking = mode === 'checking';
  // A ring set that mounts while checking paints empty for one frame, then
  // its real values, so the transition fills it from zero.
  const fillFromEmpty = useRef(checking && animate).current;
  const [armed, setArmed] = useState(!fillFromEmpty);

  useEffect(() => {
    if (armed) return;
    const frame = requestAnimationFrame(() => setArmed(true));
    return () => cancelAnimationFrame(frame);
  }, [armed]);

  const fractionFor = (area: HealthRingGeometry['area']) =>
    armed ? clampFraction(rings.find((ring) => ring.area === area)?.fraction ?? 0) : 0;

  return (
    <div
      className="tabbio-health-rings"
      style={{ width: size, height: size }}
      role={checking ? 'progressbar' : 'img'}
      aria-label={accessibilityLabel}
      aria-valuemin={checking ? 0 : undefined}
      aria-valuemax={checking ? 100 : undefined}
      aria-valuenow={checking && center.kind === 'percent' ? center.value : undefined}
    >
      <svg
        className="tabbio-health-rings-svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        aria-hidden="true"
        focusable="false"
      >
        {layout.rings.map((geometry) => {
          const fraction = fractionFor(geometry.area);
          return (
            <g key={geometry.area}>
              <circle
                className="tabbio-health-ring-track"
                cx={layout.center}
                cy={layout.center}
                r={geometry.radius}
                strokeWidth={layout.stroke}
                fill="none"
              />
              <circle
                className={`tabbio-health-ring-arc${animate ? ' is-animated' : ''}`}
                cx={layout.center}
                cy={layout.center}
                r={geometry.radius}
                stroke={geometry.cssVar}
                strokeWidth={layout.stroke}
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${geometry.circumference} ${geometry.circumference}`}
                strokeDashoffset={ringDashOffset(geometry.circumference, fraction)}
                opacity={ringArcOpacity(fraction)}
                transform={ringStartTransform(layout.center)}
              />
            </g>
          );
        })}
      </svg>
      {center.kind !== 'none' ? (
        <span className="tabbio-health-rings-center" aria-hidden="true">
          {/* dir="ltr" keeps "72%" in order inside an RTL page. */}
          <span
            className="tabbio-health-rings-value"
            dir="ltr"
            style={{ fontSize: layout.centerFontSize }}
          >
            {center.value}
            {center.kind === 'percent' ? (
              <span className="tabbio-health-rings-suffix">%</span>
            ) : null}
          </span>
        </span>
      ) : null}
    </div>
  );
}

export default HealthRings;
